'use client'
import { useImmutableChannel } from '@/api/hooks'
import { useChannelChildrenInfinite } from '@/app/(site)/[username]/channel-children'
import AddIcon from '@/app/icons/add'
import CloseIcon from '@/app/icons/close'
import { addGifsToChannel, associatedChannels, removeGifsFromChannel } from '@/app/util/channel-api'
import { FlashMessageContext } from '@/context/flash-message'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { IChannel, IGif } from '@giphy/js-types'
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'
import Loader from '../../loader'
import { ModalContext } from '../../modal'
import SearchInput from '../search-input'
import { cardClassName, ChannelContent, colorsClassName, MoreChildrenBadge } from '../ui'

const Breadcrumbs = ({
    channel,
    setChannel,
}: {
    channel: IChannel
    setChannel: Dispatch<SetStateAction<number | undefined>>
}) => {
    const { data } = useImmutableChannel(channel.id)
    const ancestors = data?.ancestors ? [...data.ancestors, channel] : [channel]
    return ancestors.map((channel, index) => {
        const isLast = index === ancestors.length - 1
        return (
            <div key={channel.id}>
                <span
                    className={cn(
                        'hover:text-giphyBlue cursor-pointer',
                        isLast && 'text-giphyLightGrey pointer-events-none'
                    )}
                    onClick={() => {
                        setChannel(channel.id)
                    }}
                >
                    {index > 0 && ' - '}
                    {channel.display_name}
                </span>
            </div>
        )
    })
}

export const AddGifToCollectionButton = ({
    className,
    children,
    gifId,
    channelId,
}: {
    className?: string
    children: React.ReactNode
    gifId?: string
    channelId: number
}) => {
    const { showStatusMessage } = useContext(FlashMessageContext)
    const { setModalType } = useContext(ModalContext)
    return (
        <div
            className={cn(className)}
            onClick={async () => {
                console.log(`gifId`, gifId)
                if (gifId) {
                    const result = await addGifsToChannel([gifId], channelId)
                    showStatusMessage('Added to collection', 'Failed to add to collection', result.ok)
                    setModalType(null)
                }
            }}
        >
            {children}
        </div>
    )
}

type Props = {
    gifs: IGif[]
    channelId: number
}

const AddToCollectionSidebar = ({ channelId: channelId, gifs }: Props) => {
    const { user } = useContext(UserContext)
    const [channel, setChannel] = useState(channelId || user?.channel_id)
    return channel ? <Collections channelId={channel} key={channel} setChannel={setChannel} gifs={gifs} /> : null
}

function getChannel(infos: { channel: IChannel; gif: IGif }[][], id: number) {
    let channel: IChannel | undefined
    infos.some((info) =>
        info.some((i) => {
            const found = i.channel.id === id
            if (found) {
                channel = i.channel
            }
            return found
        })
    )
    return channel
}

const ChannelTags = ({ gifs }: { gifs: IGif[] }) => {
    const [sharedIds, setSharedIds] = useState<number[]>([])
    const [infos, setInfo] = useState<Awaited<ReturnType<typeof associatedChannels>>[]>([])
    const { showMessage } = useContext(FlashMessageContext)
    useEffect(() => {
        const fetchThem = async () => {
            const results = await Promise.all(gifs.map((gif) => associatedChannels(gif.id)))
            const sharedIds = results
                .map((infos) => infos.map((info) => info.channel.id))
                .reduce((ids, current) => {
                    ids.forEach((id) => {
                        if (!current.includes(id)) {
                            current.push(id)
                        }
                    })
                    console.groupEnd()
                    return current
                }, [])
            setSharedIds(sharedIds)
            setInfo(results)
        }
        fetchThem()
    }, [gifs])
    return (
        <div className="flex flex-wrap gap-2">
            {sharedIds.map((id) => {
                let channel = getChannel(infos, id)
                return channel ? (
                    <div
                        key={channel.id}
                        className={
                            'bg-gradient-grey group flex shrink-0 cursor-pointer flex-nowrap items-center gap-2 rounded-3xl px-4 py-3.5 font-bold'
                        }
                        onClick={async () => {
                            let success = true
                            gifs.forEach(async (gif) => {
                                try {
                                    const r = await removeGifsFromChannel(
                                        // gifs.map((g) => g.id),
                                        [gif.id],
                                        channel.id
                                    )
                                    console.log(`r`, r)
                                    if (!r.ok) {
                                        throw `Error fetching ${r.status}`
                                    }
                                    showMessage({ message: 'Removed from channel', type: 'success' })
                                } catch (e) {
                                    success = false
                                    console.error(e)
                                    showMessage({ message: 'Error removing gifs from channel', type: 'error' })
                                }
                            })
                            if (success) {
                                setSharedIds((ids) => ids.filter((id) => id !== channel.id))
                            }
                        }}
                    >
                        <div>{channel.display_name}</div>
                        <CloseIcon className="fill-giphyWhite group-hover:fill-giphyRed size-4" />
                    </div>
                ) : null
            })}
        </div>
    )
}

const Collections = ({
    channelId,
    gifs,
    setChannel,
}: Props & {
    setChannel: Dispatch<SetStateAction<number | undefined>>
}) => {
    const { user } = useContext(UserContext)
    const { showStatusMessage } = useContext(FlashMessageContext)
    const [isAdding, setIsAdding] = useState(false)
    const { channel, isLoading, ref, children, doneFetching } = useChannelChildrenInfinite(channelId)
    return (
        <div className="isolate flex h-full flex-col gap-2 p-2">
            <ChannelTags gifs={gifs} />
            {user?.is_staff && (
                <SearchInput
                    onSelect={(channel) => {
                        setChannel(channel.id)
                    }}
                />
            )}
            {channel && (
                <div className="flex flex-col">
                    <Breadcrumbs channel={channel} setChannel={setChannel} />
                </div>
            )}

            <div
                className={cn(
                    'animate-fade-in mb-20 gap-4 pt-2',
                    isLoading ? 'flex justify-center' : 'grid grid-cols-[1fr_1fr]'
                )}
                key={`container-${isLoading}`}
            >
                {isLoading ? (
                    <Loader />
                ) : (
                    <>
                        {children?.map((subChannel) => {
                            const cClassName = cn(
                                'cursor-pointer  transition-opacity',
                                cardClassName,
                                colorsClassName,
                                isAdding && 'opacity-40 pointer-events-none'
                            )
                            return subChannel.num_children === 0 ? (
                                <div
                                    onClick={async () => {
                                        if (gifs.length > 0) {
                                            setIsAdding(true)
                                            const result = await addGifsToChannel(
                                                gifs.map((g) => g.id),
                                                subChannel.id
                                            )
                                            showStatusMessage(
                                                'Added to collection',
                                                'Failed to add to collection',
                                                result.ok
                                            )
                                            setIsAdding(false)
                                        }
                                    }}
                                    className={cn(cClassName, 'group')}
                                    key={subChannel.id}
                                >
                                    <ChannelContent channel={subChannel} />
                                    <div className="bg-giphyBlack/75 absolute inset-0 hidden flex-col items-center justify-center p-4 group-hover:flex">
                                        <div className="text-lg font-bold">Add to channel</div>
                                        <AddIcon className="fill-giphyWhite size-4" />
                                    </div>
                                </div>
                            ) : (
                                <div
                                    key={subChannel.id}
                                    className={cClassName}
                                    onClick={() => {
                                        setChannel(subChannel.id)
                                    }}
                                >
                                    <ChannelContent channel={subChannel} />
                                    <MoreChildrenBadge count={subChannel.num_children} />
                                </div>
                            )
                        })}
                        {!doneFetching && (
                            <div ref={ref} className="col-span-2 flex flex-1 justify-center">
                                <Loader />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
export default AddToCollectionSidebar
